/**
 * AI 에이전트 패널 — 자연어로 요청하면 에이전트가 카탈로그를 뒤지고, 모자란 정보는
 * 되물어 본 뒤, 실행계획으로 검증한 SQL 을 내놓는다. 결과 SQL 은 편집기에 삽입하거나
 * 바로 실행할 수 있다.
 *
 * LLM 호출 계정은 **사용자 본인 것**이다 (스탠드얼론은 PC 의 Claude Code 로그인,
 * 웹은 설정에 등록한 개인 토큰). 서버 공용 키 폴백은 없다 — 없으면 안내를 띄운다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { publishAgent, setAgentActions } from '../../utils/agentBridge';
import {
    FiMessageSquare, FiPlus, FiTrash2, FiX, FiSend, FiSquare, FiSettings, FiUser, FiPaperclip, FiEye, FiShield,
} from 'react-icons/fi';
import useAssistantThread from '../../hooks/useAssistantThread';
import MessageList from './MessageList';
import CaseSourceBar from './CaseSourceBar';
import MetaSourceRibbon from './MetaSourceRibbon';
import UsageBar from './UsageBar';
import { observe, observationScreen } from '../../utils/agentObserver';
import ObservationFeed from './ObservationFeed';
import { openGuardrail } from '../guardrail/GuardrailHost';
import { describeAccount } from './accountBadge';
import { TUNING_LEVELS, normalizeTuningLevel } from '../../utils/tuningLevels';
import { onAssistantEvent, uploadTuningSources, rememberAttachments, getUsageGuide } from '../../services/assistantClient';
import { stepHelp } from '../../help/helpWorkflow';
import { collectDroppedFiles, hasDraggedFiles } from '../../utils/dropFiles';
import PanelHeader from '../common/PanelHeader';
import { topSources } from '../../utils/sourceTree';
import { rememberLastThread, forgetLastThread, readLastThread, threadToRestore } from '../../utils/lastThread';
import { useConfirm } from '../ConfirmDialog';
import styles from './AssistantPanel.module.css';

// 클립보드 이미지에는 쓸 만한 파일명이 없다. 서버는 **저장된 파일의 확장자**로 media type 을
// 정하므로(harness/attachments.js 의 MEDIA_TYPE) 확장자가 없으면 업로드는 되고 칩도 뜨는데
// 모델에게는 조용히 안 간다. 그래서 MIME 에서 확장자를 만들어 붙이고, 여기 없는 형식은
// 올리기 전에 거절한다.

const PASTE_EXT = { 'image/png': '.png', 'image/jpeg': '.jpg', 'image/gif': '.gif', 'image/webp': '.webp' };

const stamp = () => {
    const d = new Date();
    const p = (n, w = 2) => String(n).padStart(w, '0');
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};

/** Quick Start 예시 — 어느 연결에서든 도는 카탈로그 질의. 사용자 테이블을 가정하면 첫 실습에서 깨진다. */
const QUICK_SAMPLE = {
    oracle: 'SELECT owner, table_name, num_rows\nFROM all_tables\nWHERE ROWNUM <= 20\nORDER BY owner, table_name',
    postgresql: 'SELECT schemaname, tablename\nFROM pg_tables\nWHERE schemaname NOT IN (\'pg_catalog\', \'information_schema\')\nORDER BY 1, 2\nLIMIT 20',
    default: 'SELECT 1 AS ok',
};
const sampleFor = (dbType) => QUICK_SAMPLE[dbType === 'tibero' ? 'oracle' : dbType] || QUICK_SAMPLE.default;

let localSeq = 0;
const nextLocalId = () => `help_${++localSeq}`;

function AssistantPanel({
    open, onClose, settings, onSkillLevelChange, onTuningLevelChange, activeContext, onInsertSql, onRunSql, onExplainSql, onOpenSettings, prefill, onQuickAction,
    onOpenCaseDraft,
    onOpenChallengeDraft,
    onSqlTuningEvent,
    onOpenScreen,
    // 에이전트가 잰 계획·비교 결과를 실행계획 탭으로 넘기는 통로.
    // **App 에서 useCallback 으로 고정해야 한다** — 매 렌더 새 함수면 위 이벤트 탭이
    // 다시 붙어 스트리밍 도중에 온 계획을 놓친다(onOpenCaseDraft 와 같은 함정).
    onAgentPlanCapture,
    onAgentPlanCompare,
    onAgentTurnStart,
}) {
    const { t } = useTranslation();
    const [input, setInput] = useState('');
    const [includeSelection, setIncludeSelection] = useState(false);
    // **다음 메시지에 붙을** 첨부. 보내면 비워지고 그 메시지의 말풍선에 남는다.
    const [caseSources, setCaseSources] = useState([]);
    const [uploading, setUploading] = useState(false);
    // 토큰 사용량 — 공통 훅이 done 의 이 필드를 버리므로 이벤트 탭으로 직접 받는다
    const [usage, setUsage] = useState(null);
    const [guardCheck, setGuardCheck] = useState(null);   // 마지막 턴의 가드레일 검사 결과(바꿈·뺌·그대로 · 처음 사용)
    // 리본에서 끈 정보원. 서버가 그 도구를 아예 등록하지 않는다.
    const [disabledMeta, setDisabledMeta] = useState([]);
    // 도움말 — 공통 훅의 메시지 목록에는 끼워 넣을 수 없으므로(패키지는 안 고친다) 로컬 목록을 따로
    // 두고 MessageList 가 anchor 위치에 끼워 그린다. 스레드를 바꾸면 비운다(이전 화면의 것이다).
    const [helpState, setHelpState] = useState(null);
    const [extras, setExtras] = useState([]);
    const [guide, setGuide] = useState([]);
    // 작업 감시(SSE watch) — 에이전트가 실행시킨 장시간 작업을 감시 에이전트가 지켜보는 동안의 상태(원칙 8). 끝나면 걷는다
    const [watch, setWatch] = useState(null);
    const helpHintRef = useRef('');            // 다음 턴에만 실을 도움말 힌트
    const screenCtxRef = useRef(null);         // 다른 창에서 온 요청에 딸린 그 창의 내용 — 다음 턴에만 싣는다
    const handoffRef = useRef(null);           // AI 를 바꿔 새 대화로 옮길 때의 앞 대화 id — 새 대화의 첫 턴에만 싣는다
    const pendingSuggestRef = useRef(null);      // suggest_actions 가 낸 버튼 — 턴이 끝나면 붙인다
    const inputRef = useRef(null);
    // 턴이 **시작조차 못 하고** 실패했을 때 되돌릴 질문.
    // 스트림이 한 번이라도 열렸으면 턴은 실제로 돈 것이라 되돌리지 않는다.
    const pendingTextRef = useRef('');
    const pendingAttachRef = useRef([]);         // 보낸 첨부 — turn_start 에서 메시지에 귀속시킨다
    const streamOpenedRef = useRef(false);
    const lastErrorRef = useRef(null);

    const sourceIds = useMemo(() => caseSources.filter(s => s.id).map(s => s.id), [caseSources]);
    const effectiveSettings = useMemo(
        () => ({
            ...settings, includeSelection, sourceIds, disabledMetaSources: disabledMeta,
            // 함수로 넘긴다 — 힌트는 한 턴만 실리고 지워져야 하는데, 값으로 넣으면 리렌더 전까지 남는다
            getHelpHint: () => { const h = helpHintRef.current; helpHintRef.current = ''; return h; },
            getScreenContext: () => { const s = screenCtxRef.current; screenCtxRef.current = null; return s; },
            getHandoff: () => { const h = handoffRef.current; handoffRef.current = null; return h; },
        }),
        [settings, includeSelection, sourceIds, disabledMeta],
    );

    // **접혀 있어도 늘 작동한다**(2026-10-20 사용자 요구) — 계정·대화 목록은 앱이 뜰 때 읽고, 진행 중인 답은 접어도 끊기지 않는다.
    // 예전에는 닫으면 이 컴포넌트가 내려가(언마운트) 공통 훅이 진행 중 스트림을 끊었고(서버 턴도 abort), 닫힌 동안 온 사례 초안·계획 이벤트를 버렸다
    const thread = useAssistantThread({ enabled: true, activeContext, settings: effectiveSettings });
    const { account, busy, messages, streamText, threads, threadId } = thread;

    // **새로 고쳐도 보던 대화에서 이어진다**(utils/lastThread). 대화 내용은 서버에 있다 — 여기는 어느 대화를 보고 있었는지만 기억했다가,
    // 목록을 받은 뒤 한 번 다시 연다. 직접 「새 대화」 를 고르면 잊는다. 보내는 중(busy)에는 끼어들지 않는다 — 방금 시작한 새 대화를 덮는다.
    const restoredRef = useRef(false);
    const openThreadFn = thread.openThread;
    useEffect(() => { if (threadId) rememberLastThread(threadId); }, [threadId]);
    useEffect(() => {
        if (restoredRef.current || busy || !threads || !threads.length) return;
        restoredRef.current = true;
        const id = threadToRestore(readLastThread(), threads, threadId);
        if (id) openThreadFn(id);
    }, [threads, threadId, busy, openThreadFn]);
    const newThreadFn = thread.newThread;
    const startNewThread = useCallback(() => { forgetLastThread(); restoredRef.current = true; return newThreadFn(); }, [newThreadFn]);
    // 대화 지우기 — 되돌릴 수 없어서 먼저 묻는다(예전에는 휴지통을 누르는 순간 지워졌다). 지우면 서버에서도 사라진다
    const confirm = useConfirm();
    const removeThreadFn = thread.removeThread;
    const deleteThread = useCallback(async () => {
        if (!threadId) return;
        const cur = (threads || []).find(x => x.id === threadId);
        const ok = await confirm({
            title: t('assistant:panel.deleteThread'),
            message: t('assistant:panel.deleteThreadConfirm', { title: (cur && cur.title) || t('assistant:panel.untitled') }),
            confirmText: t('common:ui.delete'),
            cancelText: t('common:ui.cancel'),
            variant: 'danger',
        });
        if (!ok) return;
        forgetLastThread();
        removeThreadFn(threadId);
    }, [threadId, threads, confirm, removeThreadFn, t]);

    // 공통 훅은 자기가 아는 이벤트만 처리하고 나머지를 버린다. LiveSQL 에만 있는
    // case_draft(사례 초안)·warning(임시 인덱스 잔존)은 클라이언트 탭으로 받는다.
    useEffect(() => {
        // 접혀 있어도 받는다 — 닫힌 동안 온 사례 초안·실행계획·경고를 버리지 않게
        return onAssistantEvent((ev) => {
            // 한 이벤트라도 왔으면 턴이 시작된 것 — 단 오류는 빼고 센다: 보내기 전 검사에서 취소한 것처럼 턴이 열리지 않은 채 오는 오류가 있고,
            // 그때는 질문을 입력창에 되돌려 고쳐 보내게 해야 한다(2026-10-20)
            if (ev.type !== 'error') streamOpenedRef.current = true;
            // 턴이 열리기 전에 온 오류(보내기 전 검사 취소 등) — 질문을 입력창에 되돌린다. 같은 오류 글이 되풀이되면 오류 상태가 안 바뀌어
            // 아래 thread.error 효과가 다시 돌지 않으므로 여기서도 받는다
            else if (!streamOpenedRef.current && pendingTextRef.current) {
                const back = pendingTextRef.current;
                pendingTextRef.current = '';
                setInput(prev => prev || back);
            }
            if (ev.type === 'done') {
                // 사용량은 언제나 보여 준다(결정 ⑧). 없는 턴(에러 등)에는 앞 값을 유지한다.
                if (ev.data && ev.data.usage) setUsage(ev.data.usage);
                // 가드레일 검사 결과 한 줄 — 걸린 것이 없으면 그렇다고, 있으면 바꾼·뺀 수를(보내기 전 검사, 2026-10-20)
                if (ev.data && ev.data.guardrail) setGuardCheck({ ...ev.data.guardrail, firstUse: !!ev.data.guardrailFirstUse });
            } else if (ev.type === 'suggestions') {
                // 답이 끝난 뒤에 붙여야 답 아래에 온다 — 지금은 스트리밍 중이다
                pendingSuggestRef.current = (ev.data && ev.data.options) || null;
            } else if (ev.type === 'turn_start') {
                // 서버가 이 메시지를 받아 id 를 정해 준 순간. 여기서야 첨부를 **소비**한다 —
                // 그 메시지에 귀속시키고 대기 줄을 비운다. 보낼 때 비우면 안 되는 이유:
                // 새 스레드면 send 가 newThread() 를 기다린 **뒤에** getContext() 를 읽으므로
                // 그 사이 비워진 목록이 실려 첫 메시지의 첨부가 사라진다. 그리고 turn_start 전에
                // 실패하면 첨부가 그대로 남으니 되돌리기가 따로 필요 없다.
                if (onAgentTurnStart) onAgentTurnStart();
                setWatch(null);   // 앞 턴의 감시 표시가 끝 이벤트를 놓쳐 남아 있지 않게
                rememberAttachments(ev.data && ev.data.userMessageId, pendingAttachRef.current);
                if (pendingAttachRef.current.length) setCaseSources([]);
                pendingAttachRef.current = [];
            } else if (ev.type === 'case_draft') {
                // **대화에서 나온 사례를 등록 화면에 채워서 연다.** 예전에는 마크다운을 편집기
                // 탭에 뿌렸는데, 거기서는 저장할 수가 없어 사용자가 다시 모델에게 "저장해" 라고
                // 말해야 했다. 등록 화면은 고치고 그 자리에서 저장한다 — 검증도 같은 것을 쓴다.
                // 창은 **App 이 그린다** — 패널이 닫히면 여기 있던 모달도 같이 사라진다.
                const d = ev.data || {};
                onOpenCaseDraft({
                    ...(d.fields || {}),
                    // 기존 사례를 고친 양식이면 그 사례 id — 모달이 새 사례가 아니라 그 사례의 편집으로 연다
                    ...(d.caseId ? { caseId: d.caseId } : {}),
                    topics: d.topics || [],
                    visibility: d.visibility || 'generic',
                    redaction: d.redaction || null,
                    nonce: Date.now(),      // 같은 내용을 다시 띄워도 화면이 알아채야 한다
                });
                if (d.note) toast.info(d.note);
                // 첨부가 있는데 화면의 초안 절차 없이 에이전트가 채웠다 — 그림·사례 나누기를 건너뛴 초안일 수 있다(서버가 본다)
                if (d.attachSkipped) toast.warning(t('assistant:panel.draftSkippedAttach', { count: d.attachSkipped }), { autoClose: 12000 });
            } else if (ev.type === 'handoff') {
                // 앞 대화의 기록이 이 턴에 실렸다 — 조용히 실으면 무엇을 알고 답하는지 모른다
                const d = ev.data || {};
                toast.info(t('assistant:panel.handoffDone', { title: d.fromTitle || '', turns: d.turns || 0 }));
            } else if (ev.type === 'case_figures') {
                // 대화의 이미지를 기존 사례의 그림으로(add_case_figures) — 그 사례의 편집 화면을 그림 탭에서 연다. 저장은 사용자가
                const d = ev.data || {};
                if (d.caseId) onOpenCaseDraft({ addFigures: { caseId: d.caseId, figures: d.figures || [] }, nonce: Date.now() });
            } else if (ev.type === 'case_register') {
                // 첨부에서 사례 등록 — 등록 화면을 **첨부를 실은 채** 연다(양식은 비어 있다). 화면의 절차가 초안을 만든다:
                // 대화에서 따로 채우면 문서 안 그림·여러 사례 나누기·공개 범위 확인을 건너뛴다(open_case_register)
                const d = ev.data || {};
                onOpenCaseDraft({ register: true, sources: d.sources || [], instruction: d.instruction || '', nonce: Date.now() });
            } else if (ev.type === 'challenge_draft') {
                // 에이전트가 채운 챌린지 초안 — 「새 챌린지」 편집기로(창은 App 이 연다). 저장은 사용자가 편집기에서
                const d = ev.data || {};
                if (onOpenChallengeDraft) onOpenChallengeDraft(d);
                if (d.note) toast.info(d.note);
            } else if (ev.type === 'plan_capture') {
                // **에이전트가 잰 계획을 실행계획 탭으로.** 도구 결과 미리보기는 2000자에서
                // 잘려서, 지금까지 사용자는 모델이 본문에 옮겨 적어 준 것만 볼 수 있었다.
                if (onAgentPlanCapture) onAgentPlanCapture(ev.data || {});
            } else if (ev.type === 'plan_compare') {
                // 결론 — 개선 전/후를 나란히 보는 탭
                if (onAgentPlanCompare) onAgentPlanCompare(ev.data || {});
            } else if (ev.type === 'warning') {
                // 임시 인덱스를 못 지운 경우 등 — 놓치면 남의 스키마에 유령이 남는다
                toast.warn(ev.data && ev.data.message ? ev.data.message : t('assistant:warning.generic'));
            } else if (ev.type === 'watch') {
                const d = ev.data || {};
                setWatch(d.state === 'ended' ? null : (prev) => ({ ...(prev || {}), ...d }));
                // 감시 에이전트가 작업을 멈췄으면 상시 관찰에도 남긴다(누가·왜 멈췄는지)
                if (d.state === 'stopped') observe({ kind: 'agentWarning', severity: 'warn', title: t('assistant:obs.watchStopped', { label: d.label || d.tool || '' }), detail: d.reason || '', source: 'watch' });
            } else if (ev.type === 'open_screen') {
                // 채팅 에이전트가 작업 창(SQL 작성·품질 검사·진단 리포트·테이블 분석)을 연다 — 창은 App 이 그린다(가이드라인 원칙 6)
                if (onOpenScreen) onOpenScreen(ev.data || {});
            } else if (ev.type === 'sql_tuning') {
                // 채팅 에이전트가 SQL 튜닝 작업을 만들거나 바꿨다(sql_tuning_* 도구) — 창은 App 이 그린다(가이드라인 원칙 6)
                if (onSqlTuningEvent) onSqlTuningEvent(ev.data || {});
            }
        });
        // onOpenCaseDraft 는 App 에서 useCallback 으로 고정한다 — 매 렌더 새 함수면
        // 이벤트 탭이 그때마다 다시 붙는다(도중에 온 이벤트를 놓친다).
    }, [open, t, onOpenCaseDraft, onOpenChallengeDraft, onSqlTuningEvent, onOpenScreen, onAgentPlanCapture, onAgentPlanCompare, onAgentTurnStart]);

    // 턴이 시작조차 못 하고 실패했으면 질문을 입력창에 되돌린다.
    // 공통 훅은 보내기 **전에** 사용자 메시지를 대화창에 넣으므로, 그냥 두면 질문은
    // 입력창에서 사라지고 대화창에는 답 없는 질문만 남는다.
    useEffect(() => {
        const err = thread.error;
        if (err && err !== lastErrorRef.current && !streamOpenedRef.current && pendingTextRef.current) {
            // 값을 먼저 꺼내 둔다 — 갱신 함수는 나중에 돌아서 그때는 ref 가 이미 비어 있다(실측: 취소 뒤 질문이 사라졌다)
            const back = pendingTextRef.current;
            setInput(prev => prev || back);
            pendingTextRef.current = '';
            if (inputRef.current) inputRef.current.focus();
        }
        lastErrorRef.current = err;
    }, [thread.error]);

    // 첨부의 **유일한 입구.** 파일 선택도 클립보드 붙여넣기도 여기로 들어온다.
    const addSources = useCallback(async (files) => {
        if (!files.length) return;
        setUploading(true);
        try {
            const r = await uploadTuningSources(files);
            const added = r.sources || [];
            setCaseSources(prev => [...prev, ...added]);

            const failed = added.filter(s => s.error);
            if (failed.length) toast.warn(t('assistant:case.uploadPartial', { count: failed.length }));
            // 문서에서 꺼낸 그림은 건수에 넣지 않는다 — 붙인 것은 파일 하나다
            else toast.success(t('assistant:case.uploaded', { count: topSources(added).length }));
        } catch (err) {
            toast.error(err.message);
        } finally {
            setUploading(false);
        }
    }, [t]);

    // 클립보드 이미지 붙여넣기.
    //
    // 텍스트가 함께 들어 있으면 **텍스트를 붙여 넣는다.** 엑셀·리치 편집기에서 복사하면
    // 그림과 글이 같이 실리는데, 대화창에 붙이려던 것은 대개 글이다.
    const onPaste = useCallback((e) => {
        const cd = e.clipboardData;
        if (!cd) return;
        const imgs = [...(cd.items || [])].filter(it => it.kind === 'file' && it.type.startsWith('image/'));
        if (imgs.length === 0) return;
        if (String(cd.getData('text/plain') || '').trim()) return;
        e.preventDefault();

        const at = stamp();
        const files = [];
        const bad = [];
        imgs.forEach((it, i) => {
            const ext = PASTE_EXT[it.type];
            if (!ext) { bad.push(it.type); return; }
            const blob = it.getAsFile();
            if (!blob) return;
            const name = `${t('assistant:case.pastedName')}-${at}${imgs.length > 1 ? `-${i + 1}` : ''}${ext}`;
            files.push(new File([blob], name, { type: it.type }));
        });
        if (bad.length) toast.warn(t('assistant:case.pasteUnsupported', { types: bad.join(', ') }));
        if (files.length) addSources(files);
    }, [addSources, t]);

    // 파일 끌어다 놓기(사용자 요청 2026-09-13) — 붙여넣기처럼 **패널 전체**에서 받고, 첨부는 같은 입구(addSources)로.
    // 파일을 끌 때만 반응한다: 입력란 안에서 글자를 끌어 옮기는 것까지 가로채면 안 된다.
    // dragenter/leave 는 자식 요소를 지날 때마다 오므로 깊이를 세야 표시가 깜빡이지 않는다.
    const [dragOver, setDragOver] = useState(false);
    const dragDepthRef = useRef(0);
    const onDragEnter = useCallback((e) => {
        if (!hasDraggedFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepthRef.current += 1;
        setDragOver(true);
    }, []);
    const onDragOver = useCallback((e) => {
        if (!hasDraggedFiles(e.dataTransfer)) return;
        e.preventDefault();                                   // 막지 않으면 drop 이 오지 않고 브라우저가 파일을 연다
        e.dataTransfer.dropEffect = busy ? 'none' : 'copy';
    }, [busy]);
    const onDragLeave = useCallback((e) => {
        if (!hasDraggedFiles(e.dataTransfer)) return;
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setDragOver(false);
    }, []);
    const onDrop = useCallback((e) => {
        if (!hasDraggedFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepthRef.current = 0;
        setDragOver(false);
        // 응답 중에는 파일 선택 버튼처럼 받지 않는다
        if (busy) { toast.info(t('assistant:composer.dropBusy')); return; }
        const { files, rejected, folders } = collectDroppedFiles(e.dataTransfer);
        if (folders) toast.warn(t('assistant:case.dropFolder'));
        if (rejected.length) toast.warn(t('assistant:case.dropUnsupported', { names: rejected.join(', ') }));
        if (files.length) addSources(files);
    }, [busy, addSources, t]);

    // 안내 데이터 — 도움말 창과 같은 것. 패널을 열 때 한 번, DBMS 가 바뀌면 다시(예시 SQL 이 다르다).
    useEffect(() => {
        if (!open) return;
        getUsageGuide(activeContext.dbType).then(r => setGuide(r.topics || [])).catch(() => setGuide([]));
    }, [open, activeContext.dbType]);

    // 스레드를 바꾸면 도움말 대화는 이전 화면의 것이다
    useEffect(() => { setExtras([]); setHelpState(null); }, [threadId]);

    // 스레드를 바꾸면 사용량도 그 스레드의 것이다 — 앞 대화의 숫자가 남아 있으면 오해한다.
    // 서버가 다음 done 에 누적값을 실어 주므로 여기서는 비우기만 한다.
    useEffect(() => { setUsage(null); }, [threadId]);

    // 제안 버튼은 턴이 끝난 **뒤** 마지막 답 아래에
    useEffect(() => {
        if (busy || !pendingSuggestRef.current) return;
        const options = pendingSuggestRef.current;
        pendingSuggestRef.current = null;
        setExtras(prev => [...prev, { id: nextLocalId(), kind: 'help', role: 'assistant', content: '', options, anchor: messages.length }]);
    }, [busy, messages.length]);

    // 메뉴·도움말 창에서 넘어온 프리필 — 입력란에 채우기만 한다. 사용자가 고쳐 보낼 수 있게.
    useEffect(() => {
        if (!open || !prefill || !prefill.text) return;
        setInput(prefill.text);
        // SQL 튜닝 탭에서 수준까지 정해 넘어왔으면 그대로 반영한다. 거기서 고른 것을 여기서
        // 다시 고르게 하면 두 화면이 서로 다른 수준을 말하게 된다.
        if (prefill.tuningLevel && onTuningLevelChange) onTuningLevelChange(normalizeTuningLevel(prefill.tuningLevel));
        if (inputRef.current) inputRef.current.focus();
    }, [open, prefill, onTuningLevelChange]);

    const tuningLevel = normalizeTuningLevel(settings.tuningLevel);
    const canRun = !!(activeContext.sessionId && activeContext.connectionId);

    /**
     * **이 대화가 실제로 도는 AI.**
     *
     * 스레드는 만들 때의 제공자를 그대로 들고 간다(서버 `thread.provider`) — 세션 재개는
     * 그 제공자의 것이라 도중에 바꿀 수 없다. 그런데 화면은 **설정값**을 보여 주고 있었다.
     * 그래서 설정을 Claude 로 바꾼 뒤에도 열린 대화는 Codex 로 돌고, 사용자에게는
     * "클로드를 쓰는데 코덱스 오류가 난다" 로 보였다(실제 신고 2026-09-11).
     *
     * 실행과 표시가 갈리면 안 된다 — 서버가 쓸 것을 그대로 보여 주고, 다르면 알린다.
     */
    // 이어 갈 세션이 아직 없는 대화는 다음 턴에 서버가 설정의 AI 로 옮긴다(threadRunner, 2026-10-29) — 그때는 묶인 것이 아니라 알리지 않는다
    const threadProvider = useMemo(() => {
        const cur = (threads || []).find(x => x.id === threadId);
        return cur && cur.provider && cur.sdkSessionId ? cur.provider : null;
    }, [threads, threadId]);
    const settingProvider = settings.provider || 'claude';
    const providerMismatch = !!threadProvider && threadProvider !== settingProvider;
    const canExplain = !!activeContext.connectionId;

    const actions = useMemo(() => ({
        canRun,
        canExplain,
        onInsert: (sql) => { onInsertSql(sql); toast.success(t('assistant:sql.inserted')); },
        onRun: (sql) => onRunSql(sql),
        onExplain: (sql) => onExplainSql(sql),
        onCopy: (sql) => {
            navigator.clipboard.writeText(sql).then(
                () => toast.success(t('assistant:sql.copied')),
                () => toast.error(t('assistant:sql.copyFailed')),
            );
        },
    }), [canRun, canExplain, onInsertSql, onRunSql, onExplainSql, t]);

    /** 도움말 상태기계의 동작 — 에이전트 도구와 **같은** 실행 경로(actions)를 쓴다. 따로 만들지 않는다. */
    const runHelpActions = useCallback(async (list) => {
        for (const a of list || []) {
            // 연결 창·세션·탭은 App 이 가진 상태라 App 의 핸들러로 — 여기서 두 번째 구현을 만들지 않는다
            if (a.type === 'open_connections' || a.type === 'open_session' || a.type === 'new_tab') {
                if (!onQuickAction) continue;
                // eslint-disable-next-line no-await-in-loop -- 순서대로 끝나야 다음 동작이 그 위에 선다 (세션 → 탭)
                const ok = await onQuickAction(a);
                if (!ok) toast.warn(t('assistant:help.actionFailed'));
            } else if (a.type === 'insert_sql') actions.onInsert(a.sql);
            else if (a.type === 'run_sql') { if (actions.canRun) actions.onRun(a.sql); else toast.warn(t('assistant:help.needSession')); }
            else if (a.type === 'explain_sql') { if (actions.canExplain) actions.onExplain(a.sql); else toast.warn(t('assistant:help.needConnection')); }
        }
    }, [actions, onQuickAction, t]);

    /**
     * 도움말 상태기계를 LLM 보다 먼저 거친다. 사용자 말풍선은 로컬로 그리고, 안내는 정해진 순서로 낸다.
     * agentText 가 있으면 상태를 유지한 채 **원문**을 서버로 보내고 힌트만 컨텍스트로 붙인다.
     * @returns true 면 여기서 끝(서버로 안 보낸다)
     */
    const tryHelp = useCallback((text) => {
        const r = stepHelp(helpState, text, {
            t, topics: guide, sample: sampleFor(activeContext.dbType),
            connectionName: activeContext.quickConnectionName || activeContext.connectionName,
            hasSession: !!activeContext.sessionId,
        });
        if (!r.handled) return false;
        setHelpState(r.state);
        if (r.agentText) { helpHintRef.current = r.agentText; return false; }   // 원문은 서버로
        const anchor = messages.length;
        const add = [
            { id: nextLocalId(), kind: 'help', role: 'user', content: text, anchor },
            ...r.messages.map(m => ({ id: nextLocalId(), kind: 'help', role: 'assistant', content: m.text, options: m.options, anchor })),
        ];
        setExtras(prev => [...prev, ...add]);
        if (r.actions) runHelpActions(r.actions);
        return true;
    }, [helpState, guide, activeContext.dbType, activeContext.connectionName, activeContext.quickConnectionName,
        activeContext.sessionId, messages.length, runHelpActions, t]);

    /**
     * 보내기 — 타이핑이든 안내 버튼이든 같은 길이다. 순서:
     *   1) 도움말 상태기계 (LLM 보다 먼저, 연결이 없어도 된다)
     *   2) 연결 검사 — 없으면 보내 봐야 서버가 거절하고 질문만 사라진다
     *   3) 첨부를 이 메시지에 싣고 서버로. 목록은 turn_start 에서 비운다(위 탭 주석 참조)
     * @returns true 면 입력을 비워도 된다
     */
    const sendText = useCallback((text) => {
        if (!text || busy) return false;
        // 앞선 안내·제안 버튼은 지나간 선택지다 — 남겨 두면 어느 것이 지금 것인지 모른다
        setExtras(prev => prev.map(x => (x.options && !x.used ? { ...x, used: true } : x)));
        if (tryHelp(text)) return true;
        // **연결이 없어도 보낸다.** 예전에는 여기서 막아, 연결을 안 붙였으면 "무엇을 할 수 있나"
        // 조차 물어볼 수 없었다(실제 신고 2026-09-11). 개념·문법·사용법처럼 DB 가 필요 없는
        // 질문이 많고, DB 가 필요한 순간에는 **서버의 도구가** 연결을 붙여 달라고 안내한다.
        pendingTextRef.current = text;
        pendingAttachRef.current = caseSources.filter(x => x.id).map(x => ({ id: x.id, filename: x.filename, kind: x.kind, parentId: x.parentId || undefined }));
        streamOpenedRef.current = false;
        thread.send(text);
        return true;
    }, [busy, thread, caseSources, tryHelp]);

    const submit = useCallback(() => {
        if (sendText(input.trim())) setInput('');
    }, [input, sendText]);

    /**
     * 다른 창과의 다리(utils/agentBridge) — 창이 패널을 가려도 같은 대화의 상태를 보고·멈추고·말을 걸 수 있게 한다.
     * 다른 창에서 온 요청은 도움말 상태기계를 거치지 않는다(그 창의 내용을 두고 하는 요청이다). 그 창의 내용은 그 턴에만 함께 간다.
     */
    const sendFromScreen = useCallback((text, opts = {}) => {
        if (!text || busy) return false;
        screenCtxRef.current = opts.screen || null;
        pendingTextRef.current = text;
        pendingAttachRef.current = [];
        streamOpenedRef.current = false;
        thread.send(text);
        return true;
    }, [busy, thread]);
    // 동작은 **한 번만** 올리고 최신 함수는 ref 로 부른다 — 의존성에 넣으면 렌더마다 내렸다 올리면서 다리의 상태(available)가 꺼진 채 남는다
    const bridgeRef = useRef({ send: sendFromScreen, stop: thread.stop });
    bridgeRef.current = { send: sendFromScreen, stop: thread.stop };
    // 다리도 접힌 동안 살아 있다 — 다른 창의 에이전트 줄·단추 줄이 접힌 에이전트의 상태를 보고 말을 건다
    useEffect(() => {
        setAgentActions({ send: (text, opts) => bridgeRef.current.send(text, opts), stop: () => bridgeRef.current.stop() });
        return () => setAgentActions(null);
    }, []);
    useEffect(() => {
        const last = [...(messages || [])].reverse().find(m => m && m.role === 'assistant' && typeof m.content === 'string' && m.content.trim());
        publishAgent({
            // 답을 기다리는 되묻기·승인(ask_user·연결 확인·execute_sql) — 접혀 있으면 단추 줄이 패널을 펼친다(안 보이면 10분 뒤 시간 초과로 「실행 안 함」)
            waiting: !!thread.openQuestion,
            available: true, busy: !!busy, threadId: threadId || null,
            activity: busy ? t(streamText ? 'assistant:strip.writing' : 'assistant:strip.thinking') : '',
            lastReply: busy && streamText ? streamText : ((last && last.content) || ''),
        });
    }, [busy, streamText, messages, threadId, t, thread.openQuestion]);

    /**
     * 제목 옆에 붙는 계정 배지. 문구 판정은 `accountBadge.js` 한 곳에 있다 —
     * 사례 등록 화면도 같은 배지를 그리므로, 여기서 따로 판정하면 같은 계정이 두 화면에서
     * 다르게 보인다.
     */
    const acct = useMemo(() => describeAccount({ account, error: thread.error }, t), [account, thread.error, t]);

    /**
     * 열림/닫힘 전환. `open` 이 꺼져도 EXIT_MS 동안은 더 그려 두고 닫히는 모습을 보여 준다.
     *
     * `shown` 은 **두 프레임 뒤**에 켠다. 한 프레임으로는 모자랐다(신고 2026-09-20: "너무
     * 갑자기 펼쳐짐"): `setRendered(true)` 는 리액트에게 그려 달라고 부탁할 뿐이라, 첫
     * rAF 콜백이 **닫힌 상태가 화면에 칠해지기 전에** 돌 수 있다. 그러면 브라우저는 시작값을
     * 본 적이 없어 전환을 걸지 않고 툭 나타난다. 한 번 더 기다리면 닫힌 모습이 확실히 칠해진
     * 뒤에 여는 값으로 바뀐다.
     */
    // 접어도 내리지 않는다(위 「늘 작동」) — 모양만 접는다. 접힌 동안은 포커스·클릭이 들어가지 않게 inert
    const [shown, setShown] = useState(false);
    const rootRef = useRef(null);
    useEffect(() => {
        if (open) {
            let inner = 0;
            const outer = requestAnimationFrame(() => {
                inner = requestAnimationFrame(() => setShown(true));
            });
            return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
        }
        setShown(false);
        return undefined;
    }, [open]);
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        if (open) el.removeAttribute('inert'); else el.setAttribute('inert', '');
    }, [open]);

    // 안내 버튼 — 누르면 그 글을 보낸 것과 같다
    const onOption = useCallback((label) => { sendText(label); }, [sendText]);
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
        }
    };

    // 늘 그려 둔다(접혀도 작동). 서버를 부르는 것은 앱이 뜰 때 한 번(계정·대화 목록)과 사용자가 보낸 턴뿐이다 — 주기적으로 부르는 것은 없다

    const needsAccount = account && !account.loggedIn;

    return (
        // 붙여넣기는 패널 전체에서 받는다 — 입력창에만 걸면 목록을 클릭한 뒤엔 안 먹는다.
        // 창 전역에 걸지 않는 이유: SQL 편집기로 갈 붙여넣기를 가로챈다.
        <div
            ref={rootRef}
            aria-hidden={!open || undefined}
            className={`${styles.panel} ${shown ? styles.panelOpen : styles.panelClosed}`}
            onPaste={onPaste}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {/* 제목줄은 **공통 컴포넌트**다(common/PanelHeader) — 같은 레일의 다른 패널과
                모양이 갈리지 않게. 계정 배지·설정처럼 이 패널만의 것은 children 으로 넣는다. */}
            <PanelHeader icon={<FiMessageSquare size={13} />} title={t('assistant:panel.title')} onClose={onClose}>
                {/* **누구 계정으로 도는가.** 실행하기 전에 보여야 한다 — 로그인이 없으면 턴이
                    412 로 죽고, 구독 대신 API 키로 돌면 종량으로 청구된다. 둘은 사용자에게
                    다른 사실이므로 "연결됨" 하나로 뭉뚱그리지 않는다. */}
                <button
                    type="button"
                    className={acct.ok ? styles.acct : styles.acctWarn}
                    title={acct.tip}
                    onClick={() => { thread.refreshAccount(); onOpenSettings('assistant'); }}
                >
                    <FiUser size={10} />
                    <span className={styles.acctText}>{acct.label}</span>
                </button>
                <button type="button" className={styles.iconBtn} onClick={onOpenSettings} title={t('assistant:panel.settings')}>
                    <FiSettings size={13} />
                </button>
            </PanelHeader>

            {/* 상시 관찰 — 기계가 모은 일. 분석은 누를 때만 같은 대화로 보낸다(utils/agentObserver) */}
            <ObservationFeed
                visible={open}
                busy={busy}
                onAnalyze={(item) => {
                    if (!sendFromScreen(t('assistant:obs.analyzePrompt', { title: item.title }), { screen: observationScreen(item) })) {
                        toast.info(t('assistant:strip.cannotSend'));
                    }
                }}
            />
            <div className={styles.context}>
                <span className={styles.badge}>{activeContext.connectionName || t('assistant:context.noConnection')}</span>
                {activeContext.dbType ? <span className={styles.badge}>{activeContext.dbType}</span> : null}
                {/* 연결이 없는 것과 세션만 없는 것은 다르다. 앞은 **대화는 되지만 DB 는 못 본다**,
                    뒤는 SQL 실행만 안 된다. 뭉뚱그리면 사용자가 무엇을 해야 할지 모른다. */}
                {!activeContext.connectionId && <span className={styles.warn}>{t('assistant:context.noConnectionChat')}</span>}
                {activeContext.connectionId && !canRun && <span className={styles.warn}>{t('assistant:context.noSession')}</span>}
                {/* 설정을 바꿔도 **열린 대화는 만들 때의 AI 로 돈다.** 눌러서 새 대화로 옮긴다. */}
                {providerMismatch && (
                    <button
                        type="button"
                        className={styles.acctWarn}
                        title={t('assistant:panel.providerMismatchTip', {
                            running: threadProvider, setting: settingProvider,
                        })}
                        // 앞 대화의 기록을 새 대화의 첫 턴에 실어 준다 — 다른 AI 는 앞 대화를 모른 채 시작한다
                        onClick={() => { handoffRef.current = threadId || null; startNewThread(); toast.info(t('assistant:panel.handoffReady')); }}
                    >
                        {t('assistant:panel.providerMismatch', { running: threadProvider })}
                    </button>
                )}
                {/* 수준은 대화 중에 바뀐다 — 같은 분석을 다른 깊이로 다시 받고 싶을 때가 있다.
                    설정 화면까지 들어가야 하면 아무도 안 바꾼다. 저장되는 값은 한 곳이다. */}
                {/* 레이블을 붙인다(2026-10-29 사용자 요청) — 고른 값(「개발자」·「L4 …」)만으로는 무엇을 고르는 칸인지 몰랐다 */}
                <label className={`${styles.levelField} ${styles.levelFieldFirst}`}>
                <span className={styles.levelLabel}>{t('assistant:settings.skillLevel')}</span>
                <select
                    className={styles.skillSelect}
                    value={settings.skillLevel || 'developer'}
                    onChange={(e) => onSkillLevelChange(e.target.value)}
                    title={t(`assistant:skill.${settings.skillLevel || 'developer'}Hint`)}
                >
                    {['novice', 'developer', 'expert'].map(lv => (
                        <option key={lv} value={lv}>{t(`assistant:skill.${lv}`)}</option>
                    ))}
                </select>
                </label>
                {/* **튜닝 수준은 다른 축이다** — 스킬 수준은 답의 형태를, 이것은 어디까지
                    해도 되는지를 정한다. 요청 직전에 고를 수 있어야 의미가 있어서 여기 둔다. */}
                <label className={styles.levelField}>
                <span className={styles.levelLabel}>{t('assistant:tuningLevel.label')}</span>
                <select
                    className={styles.tuneSelect}
                    value={tuningLevel}
                    onChange={(e) => onTuningLevelChange(Number(e.target.value))}
                    title={t(`assistant:tuningLevel.${tuningLevel}Hint`)}
                >
                    {TUNING_LEVELS.map(lv => (
                        <option key={lv} value={lv}>{t('assistant:tuningLevel.option', { n: lv, name: t(`assistant:tuningLevel.${lv}`) })}</option>
                    ))}
                </select>
                </label>
                {account && account.mode === 'standalone' && account.email ? <span>{account.email}</span> : null}
            </div>

            {needsAccount && (
                <div className={styles.notice}>
                    <strong>{t('assistant:account.needed')}</strong>
                    {account.mode === 'standalone' ? t('assistant:account.standaloneHelp') : t('assistant:account.webHelp')}
                </div>
            )}

            <MetaSourceRibbon
                connectionId={activeContext.connectionId}
                dataModelId={settings.dataModelId}
                stdNm={settings.stdNm}
                disabled={disabledMeta}
                onDisabledChange={setDisabledMeta}
            />

            <UsageBar usage={usage} />
            {guardCheck ? (
                <div className={styles.guardLine}>
                    <FiShield size={10} />
                    <span>{(guardCheck.mask || 0) + (guardCheck.exclude || 0) + (guardCheck.send || 0) === 0
                        ? t('common:grd.checkedClean')
                        : t('common:grd.checkedCounts', { mask: guardCheck.mask || 0, exclude: guardCheck.exclude || 0, send: guardCheck.send || 0 })}</span>
                    <button type="button" className={styles.guardLink} onClick={openGuardrail}>
                        {guardCheck.firstUse ? t('common:grd.firstUseNote') : t('common:grd.reviewGuide')}
                    </button>
                </div>
            ) : null}

            <MessageList
                messages={messages}
                extras={extras}
                onOption={onOption}
                streamText={streamText}
                busy={busy}
                actions={actions}
                onAnswer={thread.answer}
                onExample={(ex) => { setInput(ex); if (inputRef.current) inputRef.current.focus(); }}
            />

            {/* 작업 감시 — 감시 에이전트가 지켜보는 작업이 있으면 무엇을·얼마나·마지막 판단을 보인다(원칙 8) */}
            {watch && (
                <div className={styles.watchBar} role="status" aria-live="polite">
                    <FiEye size={12} />
                    <span className={styles.watchText}>
                        {t('assistant:watch.status', { label: watch.label || watch.tool, sec: watch.elapsedSec || 0, limit: Math.round((watch.limitSec || 0) / 60) })}
                        {watch.decision ? ` · ${t(`assistant:watch.decision.${watch.decision}`)}` : ''}
                        {watch.reason ? ` — ${watch.reason}` : ''}
                    </span>
                </div>
            )}

            <CaseSourceBar
                sources={caseSources}
                onChange={setCaseSources}
                onAdd={addSources}
                busy={uploading}
                disabled={busy}
            />

            {/* 대화 선택은 **입력란 바로 위**에 둔다 — 무엇에 이어서 쓰는지는 쓰기 직전에 본다. */}
            <div className={styles.threadBar}>
                <select
                    className={styles.threadSelect}
                    value={threadId || ''}
                    onChange={(e) => (e.target.value ? thread.openThread(e.target.value) : startNewThread())}
                    title={t('assistant:panel.threads')}
                >
                    <option value="">{t('assistant:panel.newThread')}</option>
                    {threads.map(th => (
                        <option key={th.id} value={th.id}>{th.title || t('assistant:panel.untitled')}</option>
                    ))}
                </select>
                <button type="button" className={styles.iconBtn} onClick={() => startNewThread()} title={t('assistant:panel.newThread')}>
                    <FiPlus size={13} />
                </button>
                <button
                    type="button"
                    className={`${styles.iconBtn} ${styles.threadDelBtn}`}
                    onClick={deleteThread}
                    disabled={!threadId || busy}
                    title={t('assistant:panel.deleteThreadHint')}
                >
                    <FiTrash2 size={13} /> <span>{t('assistant:panel.deleteThreadShort')}</span>
                </button>
            </div>

            <div className={styles.composer}>
                {/* 끌고 있을 때 입력 창 위에 — 놓을 곳을 보여 준다(패널 어디에 놓아도 첨부된다) */}
                {dragOver && (
                    <div className={`${styles.dropOverlay}${busy ? ` ${styles.dropOverlayBusy}` : ''}`} aria-hidden="true">
                        <FiPaperclip size={14} /> {busy ? t('assistant:composer.dropBusy') : t('assistant:composer.dropHint')}
                    </div>
                )}
                <textarea
                    ref={inputRef}
                    className={styles.textarea}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={t('assistant:composer.placeholder')}
                />
                <div className={styles.composerBar}>
                    <label className={styles.checkbox}>
                        <input type="checkbox" checked={includeSelection} onChange={(e) => setIncludeSelection(e.target.checked)} />
                        {t('assistant:composer.includeSelection')}
                    </label>
                    <span className={styles.composerHint}>{t('assistant:composer.hint')}</span>
                    {busy ? (
                        <button type="button" className={styles.sendBtn} onClick={thread.stop}>
                            <FiSquare size={11} /> {t('assistant:composer.stop')}
                        </button>
                    ) : (
                        <button type="button" className={styles.sendBtn} onClick={submit} disabled={!input.trim()}>
                            <FiSend size={11} /> {t('assistant:composer.send')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

AssistantPanel.propTypes = {
    /** 아래 셋은 App 에서 useCallback 으로 고정해 넘긴다(이벤트 탭이 다시 붙지 않게) */
    onAgentPlanCapture: PropTypes.func,
    onAgentPlanCompare: PropTypes.func,
    onAgentTurnStart: PropTypes.func,
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    settings: PropTypes.object.isRequired,
    onSkillLevelChange: PropTypes.func.isRequired,
    onTuningLevelChange: PropTypes.func.isRequired,
    prefill: PropTypes.shape({ text: PropTypes.string, nonce: PropTypes.number, tuningLevel: PropTypes.number }),
    onQuickAction: PropTypes.func,
    activeContext: PropTypes.object.isRequired,
    onInsertSql: PropTypes.func.isRequired,
    onRunSql: PropTypes.func.isRequired,
    onExplainSql: PropTypes.func.isRequired,
    onOpenSettings: PropTypes.func.isRequired,
    // 대화에서 나온 사례 초안을 **등록 화면에 채워서 연다.** 창은 App 이 그린다.
    onOpenCaseDraft: PropTypes.func.isRequired,
    onOpenChallengeDraft: PropTypes.func,
    /** SSE sql_tuning { jobId, open, step } — 채팅 에이전트가 SQL 튜닝 작업을 만들거나 바꿨다 */
    onSqlTuningEvent: PropTypes.func,
    /** 채팅 에이전트가 작업 창을 연다(SSE open_screen) — App.handleOpenScreen */
    onOpenScreen: PropTypes.func,
};

export default AssistantPanel;
